import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { it } from 'node:test';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SharedGenericSingletonService } from '@huge-monorepo/shared/services';
import { ICustomerGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';
import { ToastrService } from 'ngx-toastr';
import { SharedTeltonikaSingletonService } from '@huge-monorepo/shared/services';
import { IHIPTeltonikaService, ICustomerTeltonikaService } from '@huge-monorepo/shared/interfaces';

@Component({
  selector: 'huge-monorepo-teltonika-task-polling-modal',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './teltonika-task-polling-modal.component.html',
  styleUrl: './teltonika-task-polling-modal.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TeltonikaTaskPollingModalComponent implements OnInit, OnDestroy {
  teltonikaService = this.sharedTeltonikaService.getService();
  isHIP = this.sharedTeltonikaService.getIsHIP();
  sessionGuid: string = '';
  devices: { Name: string, ID: number, Serial: string, Status: TeltonikaTaskState, Responses?: { Message?: string, ResponseCode?: number }[] }[] = [];
  private socket: WebSocket;
  teltonikaStates = TeltonikaTaskState;
  taskId: number = 0;
  columnsToDisplay = ['Status', 'Name', 'Serial'];
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
  expandedElement: { Name: string, ID: number, Serial: string, Status: TeltonikaTaskState, Responses?: { Message?: string, ResponseCode?: number }[] } | null;
  isWebsocketClosed: boolean = false;
  constructor(
    public dialogRef: MatDialogRef<TeltonikaTaskPollingModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sessionGuid: string, taskId: number, devices: { Name: string, ID: number, Serial: string, Status: TeltonikaTaskState }[] },
    private toastr: ToastrService,
    private sharedTeltonikaService: SharedTeltonikaSingletonService<ICustomerTeltonikaService | IHIPTeltonikaService>,
    private sharedGenericService: SharedGenericSingletonService<ICustomerGenericService | IHIPGenericService>
  ) {
    dialogRef.disableClose = true;
    this.sessionGuid = data.sessionGuid;
    this.devices = data.devices.map(device => ({
      ...device,
      Responses: [],
    }));
    this.taskId = data.taskId;
  }

  ngOnInit(): void {
    this.openWebSocket();
  }

  ngOnDestroy(): void {
    this.closeWebSocket();
  }

  openWebSocket(): void {
    this.devices.forEach((device) => {
      device.Status = TeltonikaTaskState.loading;
      device.Responses = [];
    });
    this.socket = this.teltonikaService.openTeltonikaTaskPollingWebsocket(this.sessionGuid);
    this.isWebsocketClosed = false;
    this.socket.onmessage = (event) => {

      const jsonObject = JSON.parse(event.data);
      if (jsonObject["Successes"] && jsonObject["Successes"].length > 0) {
        this.devices.forEach(device => {
          jsonObject["Successes"].forEach((item) => {
            if (item["DeviceID"] === device.ID) {
              device.Status = TeltonikaTaskState.success;
              if (item["Responses"]) {

                device.Responses = item["Responses"].map(response => {
                  let message = response["Message"];
                  if (message && message.includes("(Error stored within teltonika error logs): rpc error: code = DeadlineExceeded desc = context deadline exceeded")) {
                    message = "Request timed out";
                  }
                  let responseCode = response["ResponseCode"];
                  if (responseCode < 0) {
                    responseCode = null;
                  }
                  return {
                    Message: message,
                    ResponseCode: responseCode,
                  };
                });
              }
            }
          });
        });
      }
      if (jsonObject["Failures"] && jsonObject["Failures"].length > 0) {
        this.devices.forEach(device => {
          jsonObject["Failures"].forEach((item) => {
            if (item["DeviceID"] === device.ID) {
              device.Status = TeltonikaTaskState.failure;
              if (item["Responses"]) {
                device.Responses = item["Responses"].map(response => {
                  let message = response["Message"];
                  if (message && message.includes("(Error stored within teltonika error logs): rpc error: code = DeadlineExceeded desc = context deadline exceeded")) {
                    message = "Request timed out";
                  }
                  let responseCode = response["ResponseCode"];
                  if (responseCode < 0) {
                    responseCode = null;
                  }
                  return {
                    Message: message,
                    ResponseCode: responseCode,
                  };
                });
              }
            }
          });
        });
      };
      if (jsonObject["Cancelled"] && jsonObject["Cancelled"].length > 0) {
        this.devices.forEach(device => {
          jsonObject["Cancelled"].forEach((deviceID) => {
            if (deviceID === device.ID) {
              device.Status = TeltonikaTaskState.cancelled;
            }
          });
        });
      };
    }

    this.socket.onclose = (event) => {
      this.isWebsocketClosed = true;
    }
  }

  closeWebSocket(): void {
    if (this.socket) {
      this.socket.close(1000);
    }
  }

  abortTask(): void {
    this.teltonikaService.abortTeltonikaTask(this.taskId, this.sessionGuid).subscribe({
      next: (response) => {
        this.toastr.success('Task aborted successfully');
      },
      error: (err) => {
        this.toastr.error('Error aborting task');
      }
    });
  }

  close(): void {
    this.closeWebSocket();
    this.dialogRef.close();
  }

  expand(device) {
    if (device.Responses && device.Responses.length > 0) {
      this.expandedElement = this.expandedElement === device ? null : device
    }
  }

  retryTask() {
    this.teltonikaService.executeTeltonikaTask(this.taskId, this.devices.map((device) => device.ID)).subscribe({
      next: (resp) => {
        this.sessionGuid = resp.SessionGUID
        this.sharedGenericService.service.createToast("Task Executing", "Task is being executed again.", "success");
        this.openWebSocket();
      },
      error: (err) => {
        console.error(err);
        this.sharedGenericService.service.createToast("Failed Executing Task", "The task could not execute.", "failure");
      }
    });
  }

}

export enum TeltonikaTaskState {
  loading,
  success,
  failure,
  cancelled,
}